(ns renolit-roofing-service.layout.dropdown-menu
  (:require ["@floating-ui/react" :as floating-ui-react]
            ["react" :as react]
            [goog.object :as g]
            [reagent.core :as r]
            [renolit-roofing-service.layout.advanced-tooltip :as layout.advanced-tooltip]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.util :as util]
            [renolit-roofing-service.util.interop :as util.interop]))

(defonce menu-context (react/createContext
                       (clj->js {:getItemProps (fn [] #js {})
                                 :activeIndex nil
                                 :setActiveIndex (fn [])
                                 :setHasFocusInside (fn [])
                                 :isOpen false})))

(def MenuContextProvider (.-Provider menu-context))

(defn- set-open-wrapper
  [set-open props]
  (let [on-open-envelope (g/getValueByKeys props "floating" "onOpen")
        on-open (and on-open-envelope (util.interop/envelope->value on-open-envelope))
        on-close-envelope (g/getValueByKeys props "floating" "onClose")
        on-close (and on-close-envelope (util.interop/envelope->value on-close-envelope))]
    (fn [open?]
      (util/execute-callback (if open? on-open on-close))
      (set-open open?))))

(def menu-component
  (react/forwardRef
   (fn [props forwarded-ref]
     (let [[is-open set-open] (react/useState false)
           set-open (set-open-wrapper set-open props)
           [has-focus-inside set-has-focus-inside] (react/useState false)
           [active-index set-active-index] (react/useState nil)
           elements-ref (react/useRef #js [])
           labels-ref (react/useRef #js [])
           parent (react/useContext menu-context)
           tree (floating-ui-react/useFloatingTree)
           node-id (floating-ui-react/useFloatingNodeId)
           parent-id (floating-ui-react/useFloatingParentNodeId)
           item (floating-ui-react/useListItem)
           nested? (some? parent-id)
           contextual? (g/getValueByKeys props "contextual?")
           disabled? (g/getValueByKeys props "disabled")
           context-position (g/getValueByKeys props "contextPosition")
           {:keys [refs style context] :as use-floating}
           (layout.advanced-tooltip/use-floating
            {:nodeId node-id
             :whileElementsMounted floating-ui-react/autoUpdate
             :placement (or
                         (g/getValueByKeys props "placement")
                         (if nested? "right-start" "bottom-start"))
             :open is-open
             :onOpenChange set-open
             :middleware [(floating-ui-react/offset
                           (clj->js (if nested?
                                      {:mainAxis 0 :alignmentAxis -4}
                                      {:mainAxis 4 :alignmentAxis 0})))
                          (floating-ui-react/flip)
                          (floating-ui-react/shift)]})
           {:keys [floating-props interactions]}
           (layout.advanced-tooltip/use-interactions
            (:context use-floating)
            {:hover {:enabled (if disabled? false nested?)
                     :delay {:open 75}
                     :handleClose (floating-ui-react/safePolygon #js {:blockPointerEvents true})}
             :click {:event "mousedown"
                     :toggle (not nested?)
                     :ignoreMouse nested?}
             :role {:role "menu"}
             :dismiss {:bubbles true}
             :list-navigation {:listRef elements-ref
                               :activeIndex active-index
                               :nested nested?
                               :onNavigate set-active-index}
             :typeahead {:listRef labels-ref
                         :onMatch (when is-open set-active-index)
                         :activeIndex active-index}})]
       (react/useEffect
        (fn []
          (if tree
            (letfn [(handle-tree-click []
                      (set-open false))
                    (on-sub-menu-open [e]
                      (when (and (not= (.-nodeId e) node-id)
                                 (= (.-parentId e) parent-id))
                        (set-open false)))]
              (.on (.-events tree) "click" handle-tree-click)
              (.on (.-events tree) "menuopen" on-sub-menu-open)
              (fn []
                (.off (.-events tree) "click" handle-tree-click)
                (.off (.-events tree) "menuopen" on-sub-menu-open)))
            js/undefined))
        #js [tree node-id parent-id])
       (react/useEffect
        (fn []
          (when (and is-open tree)
            (.emit (.-events tree) "menuopen" #js {:parentId parent-id
                                                   :nodeId node-id}))
          js/undefined)
        #js [tree is-open node-id parent-id])
       (when contextual?
         (react/useEffect
          (fn []
            (when context-position
              (.setPositionReference
               refs
               #js {:getBoundingClientRect
                    (fn []
                      (let [x (.-x context-position)
                            y (.-y context-position)]
                        #js {:width 0
                             :height 0
                             :x x
                             :y y
                             :top y
                             :right x
                             :bottom y
                             :left x}))})
              (set-open true))
            (when (nil? context-position)
              (set-open false))
            js/undefined)
          #js [context-position])
         (react/useEffect
          (fn []
            (let [element-id (g/getValueByKeys props "contextElementId")
                  element (and element-id (js/document.getElementById element-id))]
              (if element
                (letfn [(on-context-menu [e]
                          (.preventDefault e)
                          (.setPositionReference
                           refs
                           #js {:getBoundingClientRect
                                (fn []
                                  #js {:width 0
                                       :height 0
                                       :x (.-clientX e)
                                       :y (.-clientY e)
                                       :top (.-clientY e)
                                       :right (.-clientX e)
                                       :bottom (.-clientY e)
                                       :left (.-clientX e)})})
                          (set-open true))]
                  (.addEventListener element "contextmenu" on-context-menu)
                  (fn []
                    (.removeEventListener element "contextmenu" on-context-menu)))
                js/undefined)))))
       (r/as-element
        [:> floating-ui-react/FloatingNode
         {:id node-id}
         (when-not contextual?
           (let [opts (merge
                       {:ref (floating-ui-react/useMergeRefs #js [(.-setReference refs)
                                                                  (.-ref item)
                                                                  forwarded-ref])
                        :tab-index (when nested?
                                     (if (= (.-activeIndex parent) (.-index item))
                                       0 -1))
                        :role (when nested? "menuitem")
                        :data-open (when is-open "")
                        :data-nested (when is-open "")
                        :data-focus-inside (when has-focus-inside "")}
                       (js->clj (.getReferenceProps
                                 interactions
                                 (.getItemProps parent
                                                (clj->js
                                                 (merge
                                                  (js->clj props)
                                                  {:onFocus (fn [e]
                                                              (when-let [on-focus (.-onFocus props)]
                                                                (on-focus e))
                                                              (set-has-focus-inside false)
                                                              (.setHasFocusInside parent true))}))))))]
             (if-let [reference-component (g/getValueByKeys props "reference" "component")]
               [reference-component
                opts
                (when-let [args (g/getValueByKeys props "reference" "args")]
                  (util.interop/envelope->value args))]
               [:button
                (-> opts
                    (assoc :className (if nested? "dropdown-menu__item" "dropdown-menu__root")))
                (g/getValueByKeys props "label")
                (when nested?
                  [layout.icon/icon
                   {:key "chevron_right"
                    :props {:aria-hidden true}}])])))
         [:> MenuContextProvider
          {:value {:activeIndex active-index
                   :setActiveIndex set-active-index
                   :getItemProps (.-getItemProps interactions)
                   :setHasFocusInside set-has-focus-inside
                   :isOpen is-open}}
          [:> floating-ui-react/FloatingList
           {:elementsRef elements-ref
            :labelsRef labels-ref}
           (when is-open
             [:> floating-ui-react/FloatingPortal
              {:id "app-container"}
              [:> floating-ui-react/FloatingOverlay
               {:lockScroll contextual?
                :style (clj->js {:position "unset"})}
               [:> floating-ui-react/FloatingFocusManager
                {:context context
                 :modal false
                 :initialFocus (if nested? -1 0)
                 :returnFocus (not nested?)}
                (let [z-index-style (doto style
                                      (aset "zIndex" 2147483647))
                      opts (merge {:ref (.-setFloating refs)
                                   :className "dropdown-menu__menu"
                                   :style z-index-style}
                                  floating-props)
                      children (.-children props)]
                  (if-let [component (g/getValueByKeys props "floating" "component")]
                    [component
                     opts
                     (when-let [args (g/getValueByKeys props "floating" "args")]
                       (util.interop/envelope->value args))
                     children]
                    [:div
                     opts
                     children]))]]])]]])))))

(def menu-item
  (react/forwardRef
   (fn [props forwarded-ref]
     (let [{:keys [label disabled?]} (js->clj props :keywordize-keys true)
           menu (react/useContext menu-context)
           item (floating-ui-react/useListItem
                 #js {:label (when-not disabled? label)})
           tree (floating-ui-react/useFloatingTree)
           active? (= (.-index item) (.-activeIndex menu))]
       (r/as-element
        (let [opts (merge
                    (js->clj props)
                    {:ref (floating-ui-react/useMergeRefs
                           #js [(.-ref item) forwarded-ref])
                     :type "button"
                     :role "menuitem"
                     :className "dropdown-menu__item"
                     :tabIndex (if active? 0 -1)}
                    (js->clj (.getItemProps menu #js {:onClick (fn [e]
                                                                 (when-let [on-click (.-onClick props)]
                                                                   (when (and (on-click e) tree)
                                                                     (.emit (.-events tree) "click"))))
                                                      :onFocus (fn [e]
                                                                 (when-let [on-focus (.-onFocus props)]
                                                                   (on-focus e))
                                                                 (.setHasFocusInside menu true))})))]
          (if-let [component (g/getValueByKeys props "component")]
            [component
             (dissoc opts "component")
             (when-let [args (g/getValueByKeys props "args")]
               (util.interop/envelope->value args))]
            [:button
             opts
             label])))))))

(def menu
  (react/forwardRef
   (fn [props forwarded-ref]
     (let [parent-id (floating-ui-react/useFloatingParentNodeId)]
       (r/as-element
        (if parent-id
          [:> menu-component
           (merge
            (js->clj props)
            {:ref forwarded-ref})]
          [:> floating-ui-react/FloatingTree
           [:> menu-component
            (merge
             (js->clj props)
             {:ref forwarded-ref})]]))))))

(defn item-separator
  []
  [:div.dropdown-menu__item-separator])
